import _, { findIndex, forEach, has, isEmpty, reduce } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import Constants from 'js/Constants';
import { Field, SourceConfig } from '@nexla/@types';
import * as ValidationUtils from 'js/lib/ValidationUtils';
import {
  API_URL_PLACEHOLDER_PATTERN,
  DEFAULT_ITERATION,
  ITERATION_TYPE_TO_FIELDS_MAP,
} from './RestAPISource.constants';
import { formatCode } from 'js/lib/FormatUtils';
import JSONUtils from 'js/lib/JSONUtils';
import { CONNECTION_CATEGORY_TEMPLATIZED_API } from 'js/constants/connectionCategories';
import { getVendorEndpointField } from 'new/components/flows/types/DirectFlow/utils';
import { filterActionEndpointOptions } from 'new/utils/actionEndpoints';

/**
 * Getters
 */
export const getIterationKey = (dataMapId = null, stageIndex = -1) => {
  return dataMapId
    ? `${Constants.IterationPrefixes.DATA_MAP}${dataMapId}`
    : `${Constants.IterationPrefixes.STAGE}${stageIndex + 1}`;
};

/**
 * The function returns a new array containing only the iterations from the provided configuration (config)
 * that do not have the key 'iteration.type' set to "data.map.key.queue".
 * @param apiConfig
 * @param iterations
 */
export const getNonDataMapIterations = (apiConfig: any, iterations = null) => {
  const config = iterations ? iterations : apiConfig['rest.iterations'];
  return _.filter(config, (iteration) => iteration['iteration.type'] !== 'data.map.key.queue');
};

const placeholder_pattern = /\$\{([\w.-]+?)\}/g;
const unresolvedPlaceholderPattern = /\$\{[\w.-]+?\}/;
/**
 * Processes a URL template by replacing macros and applying fallback logic if necessary.
 */
export const resolveURLTemplate = ({ value, key, config, fields, selectedCredential }) => {
  if (typeof value !== 'string') return value;

  let replacedMacrosValue = UIUtils.replaceMacros({
    templateURL: value,
    config: config,
    fields: fields,
    placeholder_pattern,
    selectedCredential,
  });

  const credentialTestUrl = selectedCredential?.credentials_non_secure_data?.['test.url'];

  if (
    key === 'url.template' &&
    unresolvedPlaceholderPattern.test(replacedMacrosValue) &&
    credentialTestUrl
  ) {
    replacedMacrosValue = credentialTestUrl; // use credential test URL when macros did not resolve
  }

  return replacedMacrosValue;
};

/**
 * Adds the `isPaginated` property to an iteration object if certain conditions are met.
 */
export const markAsPaginatedIterationIfApplicable = (iteration: any) => {
  if (
    has(iteration, 'iteration.type') &&
    iteration['iteration.type'] !== 'static.url' &&
    !has(iteration, 'isPaginated')
  ) {
    return { ...iteration, isPaginated: true };
  } else {
    return iteration;
  }
};

export const getIterationKeyFromIteration = (iteration: any, stageIndex = -1) => {
  return iteration['iteration.type'] === 'data.map.key.queue'
    ? getIterationKey(iteration['map.id'])
    : getIterationKey(null, stageIndex);
};

export const getFormDataFromCredential = ({
  isEditFlow = false,
  credentials_non_secure_data: credential,
  apiConfig,
  sourceConfig,
}) => {
  const formData = Object.create({ apiConfig, sourceConfig });

  if (isEditFlow) return formData;

  if (has(credential, 'test.url')) {
    formData.apiConfig = {
      ...apiConfig,
      'rest.iterations': [
        {
          ...apiConfig['rest.iterations'][0],
          'url.template': credential['test.url'],
        },
      ],
    };
  }

  return formData;
};

/**
 * Used at:
 *  - validateDataPath
 *  - buildSourceConfigFromPropsToRestAPI
 */
export const getDataPathFromAPIConfig = (config: any) => {
  const fieldNames = ITERATION_TYPE_TO_FIELDS_MAP[config['iteration.type']];
  const dataPathFieldNames = ['response.data.path'].concat(
    _.find(fieldNames, (name) => name.endsWith('.data.path')),
  );
  const result = {};

  _.forEach(dataPathFieldNames, (dataPathFieldName) => {
    if (!config[dataPathFieldName]) {
      return;
    }

    let dataPath = config[dataPathFieldName];

    if (
      config['contentType'] === 'application/json' &&
      !dataPath.startsWith('$.') &&
      !dataPath.startsWith('$[') &&
      dataPath !== '$'
    ) {
      const prefix = dataPath.charAt(0) === '[' ? '$' : '$.';
      dataPath = `${prefix}${dataPath}`;
    }

    result[dataPathFieldName] = dataPath;
  });

  return result;
};

const handleModifierIterations = (iterations = []) => {
  if (Array.isArray(iterations)) {
    return iterations.map((itStage: any) => {
      const newIteration = { ...itStage };

      // set default date.format and date.time.unit in old sources
      if (!newIteration['date.format']) {
        newIteration['date.format'] = 'yyyy-MM-dd';
      }

      if (!newIteration['date.time.unit']) {
        newIteration['date.time.unit'] = 'dd';
      }

      if (newIteration['response.data.path.additional']) {
        newIteration['response.data.path.additional'] = _.join(
          newIteration['response.data.path.additional'],
        );
      }

      return newIteration;
    });
  }
  return [];
};

export const filterFieldsBasedOnHCSourceField = (field: Field) =>
  ['start.cron', ...Constants.HC_SOURCE_FIELD_NAMES].includes(field.name);

export const setVendorInformation = (name, value, config, meta) => {
  if (name === 'advanced_templatized_api') {
    if (value) {
      config.vendor_id = meta.vendorId;
    } else {
      delete config.vendor_id;
    }
  }
};

/**************************************************************************
 ************************  API Response Stage  ****************************
 **************************************************************************
 * Used at:
 *  - RestAPIProvider
 *  - - useEffect
 */
type HandleSuccessfulAPIResponse = (
  stageIndex: string,
  response: any,
  onCallback: (...args: any) => void,
) => any;

export const handleSuccessfulAPIResponse: HandleSuccessfulAPIResponse = (
  stageIndex,
  response,
  onCallback,
) => {
  let contentType: any;
  const { response: responseString, statusCode } = response.output;
  contentType = response.output.contentType;
  const prettyResponse = formatCode(responseString, contentType);

  if (!_.isNaN(parseInt(stageIndex, 10))) {
    if (typeof onCallback === 'function') {
      onCallback('contentType', contentType, Number(stageIndex));
    }
  }

  const apiResponseParsedContent = JSONUtils.parseJson(prettyResponse);
  const apiResponseContentLength =
    apiResponseParsedContent &&
    (Array.isArray(apiResponseParsedContent)
      ? apiResponseParsedContent.length
      : typeof apiResponseParsedContent === 'object'
        ? // @ts-ignore
          Object.values(apiResponseParsedContent)?.[0]?.length
        : null);

  return {
    apiResponseContent: prettyResponse,
    apiResponseContentLength,
    apiResponseContentType: contentType,
    apiResponseCode: statusCode,
    apiResponseMessage: '',
  };
};

export const handleAPIResponseStage = ({ stageProbeSample, stageIndex, onAPIConfigChanged }) => {
  const { response, error } = stageProbeSample;

  if (response) {
    const { response: responseString, statusCode } = response.output;

    if (statusCode > 0 && statusCode < 400) {
      return handleSuccessfulAPIResponse(stageIndex, response, onAPIConfigChanged);
    }

    // 3rd party API error
    return {
      apiResponseContent: responseString,
      apiResponseContentLength: null,
      apiResponseContentType: 'text/plain',
      apiResponseCode: statusCode,
      apiResponseMessage: response.output.errorMessage,
    };
  } else if (error) {
    const responseCode = error.code;
    return {
      apiResponseContent: '',
      apiResponseContentLength: null,
      apiResponseContentType: 'text/plain',
      apiResponseCode: responseCode || 500,
      apiResponseMessage: 'Please check the URL or try again later',
    };
  }

  return {
    apiResponseContent: '',
    apiResponseContentLength: null,
    apiResponseContentType: 'text/plain',
    apiResponseCode: null,
    apiResponseMessage: null,
  };
};

/*****************************************************************************
 ******************************** BUILDERS ***********************************
 *****************************************************************************/
const buildApiConfigToRestAPI = (isEditFlow = false, data = null) => {
  if (isEditFlow) {
    let { apiConfig, sourceConfig, dataMaps, source } = data;

    if (!has(sourceConfig, 'rest.iterations')) {
      const iteration = _.omit(sourceConfig, 'start.cron');
      apiConfig = {
        'start.cron': sourceConfig['start.cron'],
        'rest.iterations': [
          {
            ...iteration,
            key: getIterationKeyFromIteration(iteration, 0),
          },
        ],
      };
    } else {
      // get the dataMaps out as dataMapIds.
      let dataMapIds = [];
      let restIterations = sourceConfig['rest.iterations'];
      let modifiedIterations = getNonDataMapIterations(undefined, restIterations);

      modifiedIterations = handleModifierIterations(modifiedIterations);

      // TODO: legacy data map iteration (from v1.6)
      let hasLegacyDM = false;
      let lookupKey = null;
      _.each(restIterations, (itStage) => {
        if (has(itStage, 'iteration.type') && itStage['iteration.type'] === 'data.map.key.queue') {
          dataMapIds.push(itStage['map.id']);
          //legacy data map iteration (from v1.6)
          if (itStage.key === 'dm1') {
            let key = getIterationKeyFromIteration(itStage);
            lookupKey = key;
            hasLegacyDM = true;
            modifiedIterations.push({ ...itStage, key });
          } else {
            modifiedIterations.push(itStage);
          }
        }
      });

      if (hasLegacyDM && lookupKey) {
        //legacy data map iteration (from v1.6)
        modifiedIterations = _.map(modifiedIterations, (itStage) => {
          if (
            has(itStage, 'iteration.type') &&
            itStage['iteration.type'] !== 'data.map.key.queue' &&
            has(itStage, 'url.template')
          ) {
            let urlTemplate = itStage['url.template'];
            //replace {dm1. in template with {lookupKey.
            urlTemplate = _.replace(urlTemplate, '{dm1.', `{${lookupKey}.`);
            return { ...itStage, 'url.template': urlTemplate };
          } else {
            return itStage;
          }
        });
      }

      dataMapIds = _.map(dataMapIds, (item) => {
        if (dataMaps && has(dataMaps, item)) {
          return {
            label: dataMaps[item].name,
            value: item,
          };
        } else {
          return {
            label: item,
            value: item,
          };
        }
      });

      let dataMapKeys = {};
      if (dataMaps) {
        _.each(dataMapIds, (m) => {
          let selectedDataMap = has(dataMaps, m.value) ? dataMaps[m.value] : null;

          let thisMapKeys = has(selectedDataMap, 'map_entry_schema.properties')
            ? _.keys(selectedDataMap.map_entry_schema.properties)
            : [];
          let keyName = getIterationKey(m.value);
          dataMapKeys[keyName] = thisMapKeys;
        });
      }

      apiConfig = {
        ...sourceConfig,
        'rest.iterations': modifiedIterations,
        dataMapIds,
        dataMapKeys,
      };
    }

    apiConfig['rest.iterations'] = _.map(apiConfig['rest.iterations'], (iteration) => ({
      ...iteration,
      isPaginated:
        iteration['iteration.type'] &&
        iteration['iteration.type'] !== 'data.map.key.queue' &&
        iteration['iteration.type'] !== 'static.url',
    }));

    const templatizedAPIConfig = source ? UIUtils.buildTemplatizedAPIConfigFromSource(source) : {};

    return { apiConfig, templatizedAPIConfig };
  }

  const defaultIteration = DEFAULT_ITERATION;
  defaultIteration.key = getIterationKeyFromIteration(DEFAULT_ITERATION, 0);

  return {
    templatizedAPIConfig: {},
    apiConfig: { 'rest.iterations': [defaultIteration] },
  };
};

const buildNewApiConfigToAddStep = (apiConfig: any) => {
  if (!apiConfig || Array.isArray(apiConfig['rest.iterations']) === false)
    return { error: true, message: 'Invalid API config' };

  const newIteration = { ...DEFAULT_ITERATION };
  const iterations = [...apiConfig['rest.iterations']];

  // insert the new element before the first data map iteration
  const dataMapIterationIndex = findIndex(
    iterations,
    (item) => item['iteration.type'] === 'data.map.key.queue',
  );

  let index: number = iterations.length;
  if (dataMapIterationIndex < 0) {
    iterations.push(newIteration);
  } else {
    index = dataMapIterationIndex;
    iterations.splice(dataMapIterationIndex, 0, newIteration);
  }

  newIteration.key = getIterationKeyFromIteration(newIteration, index);

  return {
    ...apiConfig,
    'rest.iterations': iterations,
    advanced_templatized_api: true,
  };
};

const buildSourceConfigFromPropsToRestAPI = (args: any) => {
  const {
    stageIndex = -1,
    paging: { sourceConfigById },
    currentOrg,
    selectedConnection,
    apiConfig,
    connectionCategory = 'rest',
    templatizedAPIConfig,
  } = args;

  const configFields = sourceConfigById[selectedConnection];

  let sourceConfig = _.omit(apiConfig, ['dataMapIds']);
  let iterations = sourceConfig['rest.iterations'];

  if (stageIndex > -1) {
    iterations = _.filter(
      iterations,
      (iteration, index) =>
        index <= stageIndex || iteration['iteration.type'] === 'data.map.key.queue',
    );
  }

  sourceConfig['rest.iterations'] = _.map(iterations, (iteration) => {
    const currentIterationType = iteration['iteration.type'];

    if (currentIterationType === 'data.map.key.queue') {
      return iteration;
    }

    let newIteration = { ...iteration };

    if (!newIteration.isPaginated) {
      newIteration['iteration.type'] = 'static.url';
    }

    if (newIteration['response.data.path.additional']) {
      newIteration['response.data.path.additional'] = _.split(
        newIteration['response.data.path.additional'],
        /\s*,\s*(?=\$)/,
      );
    }

    // prepend "$." to data paths
    const dataPaths = getDataPathFromAPIConfig(newIteration);

    _.forEach(dataPaths, (dataPath, fieldName) => {
      newIteration[fieldName] = dataPath;
    });

    return UIUtils.reduceConfig(_.omit(newIteration, ['isPaginated', 'contentType']), configFields);
  });

  sourceConfig['rest.iterations'] = _.sortBy(sourceConfig['rest.iterations'], (iteration) =>
    iteration['iteration.type'] === 'data.map.key.queue' ? 0 : 1,
  );

  if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API) {
    if (!apiConfig?.advanced_templatized_api) {
      sourceConfig = UIUtils.buildSourceFromTemplatizedAPIConfig(
        templatizedAPIConfig,
        configFields,
      );
    }
  }

  // populate high capacity fields
  const hcFields = _.filter(configFields, (field) =>
    Constants.HC_SOURCE_FIELD_NAMES.includes(field.name),
  );

  if (UIUtils.isHCEnabled(currentOrg.id, selectedConnection)) {
    sourceConfig = UIUtils.configWithMissingDefaults(sourceConfig, hcFields);

    _.forEach(hcFields, (field) => {
      if (!UIUtils.shouldRenderField(field, sourceConfig)) {
        delete sourceConfig[field.name];
      }
    });
  } else {
    _.forEach(hcFields, (field) => {
      delete sourceConfig[field.name];
    });
  }

  return sourceConfig;
};

const buildNewApiConfigToRemoveStep = (args: any) => {
  const { apiConfig, stageIndex = -1 } = args;
  if (!apiConfig || Array.isArray(apiConfig['rest.iterations']) === false) {
    return { error: true, message: 'Invalid API config' };
  }

  const iterations = [...apiConfig['rest.iterations']];
  iterations.splice(stageIndex, 1); // remove element from rest.iterations

  const stageAPIConfig = apiConfig['rest.iterations'][0];

  // recalculate keys of iterations following the removed iteration
  for (let i = stageIndex; i < iterations.length; i++) {
    const iteration = iterations[i];
    if (iteration['iteration.type'] === 'data.map.key.queue') break;
    iterations[i] = {
      ...iteration,
      key: getIterationKeyFromIteration(iteration, i),
    };
  }

  /**
   * if the iteration doesn't have an url.template, set it
   */
  if (iterations.length === 1) {
    if (iterations[0]['url.template'] === undefined) {
      iterations[0]['url.template'] = stageAPIConfig['url.template'];
    }
  }

  return { ...apiConfig, 'rest.iterations': iterations };
};

const buildParameterizedURLs = (props: any) => {
  const {
    apiConfig,
    dataMaps,
    stageIndex,
    dataMapSamples,
    apiResponseByStage,
    dataPathValidationResultByStage,
  } = props;

  const urlTemplate = apiConfig['rest.iterations'][stageIndex]?.['url.template'];
  const placeholders = urlTemplate?.match?.(API_URL_PLACEHOLDER_PATTERN);

  if (!placeholders || placeholders.length === 0) return null;

  const numSamples = 5;
  const placeholderValues = {};

  _.forEach(placeholders, (placeholder) => {
    if (has(placeholderValues, placeholder)) return;

    placeholderValues[placeholder] = new Array(numSamples);
    const [, iterationPrefix, iterationId, propertyName] = placeholder
      .match(/(([a-zA-Z]+)(\d+))\.(\w+)/)
      .slice(1);

    switch (iterationPrefix) {
      case Constants.IterationPrefixes.DATA_MAP:
        const dataMap = dataMaps[iterationId];

        if (!dataMap) {
          break;
        }

        const dataMapValues = dataMapSamples[dataMap.id] ? dataMapSamples[dataMap.id].flat : [];

        for (let i = 0; i < numSamples; i++) {
          if (has(dataMapValues[i], propertyName)) {
            placeholderValues[placeholder][i] = String(dataMapValues[i][propertyName]);
          }
        }

        break;

      case Constants.IterationPrefixes.STAGE:
        const iterationStageIndex = parseInt(iterationId, 10) - 1;

        if (
          iterationStageIndex < stageIndex &&
          has(dataPathValidationResultByStage, iterationStageIndex)
        ) {
          const validationResult = dataPathValidationResultByStage[iterationStageIndex];
          let response;

          if (validationResult.status) {
            try {
              response = JSON.parse(apiResponseByStage[iterationStageIndex].apiResponseContent);
            } catch (error) {
              console.log('JSON parse error', error);
            }
          } else if (has(validationResult, 'response.data.path')) {
            response = validationResult['response.data.path'].matches;
          }

          if (!response) {
            break;
          }

          for (let i = 0; i < numSamples; i++) {
            let object;

            if (_.isArray(response) && response.length > i) {
              object = response[i];
            } else if (_.isPlainObject(response)) {
              object = response;
            }

            if (!object) {
              continue;
            }

            const value = object[propertyName];

            if (_.isNumber(value) || _.isString(value) || _.isBoolean(value)) {
              placeholderValues[placeholder][i] = String(value);
            }
          }
        }

        break;

      default:
    }
  });

  // replace placeholders
  const urls = [];
  const foundURLs = [];

  for (let i = 0; i < numSamples; i++) {
    const params = [];
    let url = urlTemplate;

    for (let j = 0, lastPosition = 0; j < placeholders.length; j++) {
      const placeholder = placeholders[j];
      const placeholderValue = placeholderValues[placeholder][i];

      if (!_.isUndefined(placeholderValue)) {
        const startIndex = url.indexOf(placeholder, lastPosition);
        const endIndex = startIndex + placeholderValue.length;
        url = url.replace(placeholder, placeholderValue);
        params.push({
          placeholder,
          startIndex,
          endIndex,
        });

        lastPosition = endIndex;
      } else {
        lastPosition += placeholder.length;
      }
    }

    if (params.length > 0 && foundURLs.indexOf(url) < 0) {
      foundURLs.push(url);
      urls.push({
        url,
        params,
      });
    }
  }

  return {
    urlTemplate,
    placeholders,
    urls,
  };
};

const buildConfigToCreateRestAPISource = ({
  paging,
  apiConfig,
  sourceConfig,
  organization,
  configFields = [],
  selectedConnection,
  connectionCategory,
  templatizedAPIConfig,
}) => {
  try {
    const iterationWithURL = _.find(apiConfig['rest.iterations'], (iteration) =>
      has(iteration, 'url.template'),
    );
    const urlTemplate = iterationWithURL?.['url.template'] || apiConfig?.['url.template'];
    const URL = urlTemplate ? UIUtils.parsePartsFromURL(urlTemplate) : null;
    const sourceNameFromURL = URL
      ? URL.lastPath !== ''
        ? `${_.capitalize(URL.hostname)} - ${URL.lastPath}`
        : `${_.capitalize(URL.hostname)}`
      : null;

    let sourceName =
      sourceNameFromURL ||
      apiConfig?.name ||
      apiConfig?.sourceName ||
      sourceConfig?.name ||
      sourceConfig?.sourceName ||
      selectedConnection;

    if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API) {
      const endpoint_id = templatizedAPIConfig?.vendor_endpoint_id;

      const endpointField = _.find(configFields, {
        name: 'vendor_endpoint_id',
      });
      const endpointOption = _.find(endpointField.options, {
        value: endpoint_id,
      });
      sourceName = endpointOption ? endpointOption.label : endpoint_id.toString(10);

      return {
        sourceName,
        newSourceConfig: {
          ...RestAPIUtil.builders.buildSourceConfigFromPropsToRestAPI({
            stageIndex: -1,
            paging,
            currentOrg: organization,
            selectedConnection,
            apiConfig,
            connectionCategory,
            templatizedAPIConfig,
          }),
        },
      };
    }

    return {
      sourceName,
      newSourceConfig: {
        ...sourceConfig,
        ...RestAPIUtil.builders.buildSourceConfigFromPropsToRestAPI({
          stageIndex: -1,
          paging,
          currentOrg: organization,
          selectedConnection,
          apiConfig,
          connectionCategory,
          templatizedAPIConfig,
        }),
      },
    };
  } catch (e) {
    console.error('buildConfigToCreateRestAPISource: ', e.message);
  }
};

const buildSourceConfigToRestAPIFromDefaults = (
  sourceConfig: SourceConfig = {},
  configFields: Field[] = [],
) => {
  let currentSourceConfig = sourceConfig;
  let sourceConfigFields = configFields;

  forEach(sourceConfigFields, (f) => {
    if (has(f, 'default') && !has(currentSourceConfig, f.name)) {
      currentSourceConfig[f.name] = f.default;
    }
  });

  /**
   * omit fields which shouldn't be rendered
   */
  const visibleFieldData = UIUtils.omitInvisibleFieldData(sourceConfigFields, currentSourceConfig);

  if (isEmpty(visibleFieldData)) return sourceConfig;

  return visibleFieldData;
};

const buildIterationKey = (mapId: number) => {
  const iteration = {
    key: undefined,
    'map.id': mapId,
    'iteration.type': 'data.map.key.queue',
  };
  iteration.key = getIterationKeyFromIteration(iteration);
  return iteration;
};

/**
 * 1 - find vendor_endpoint_id
 * 2 - sort the fields
 * 3 - config the new templatizedAPIConfig
 */
const buildTemplatizedAPIConfig = ({
  fields,
  templatizedAPIConfig,
  flowType,
}: {
  fields: any[];
  templatizedAPIConfig: any;
  flowType?: string;
}) => {
  const endpointField = fields.find(getVendorEndpointField);

  const hasVendorEndpointId = !!templatizedAPIConfig?.vendor_endpoint_id;
  // Options are already in display order (see setVendorEndpointData in the paging reducer).
  // ETL sources never default to an action endpoint.
  const [defaultOption] = filterActionEndpointOptions(endpointField?.options, { flowType });

  if (defaultOption && !hasVendorEndpointId) {
    /**
     * Set the first option as vendor_endpoint_id if it's not already set.
     */
    return UIUtils.configWithMissingDefaults(
      { ...templatizedAPIConfig, vendor_endpoint_id: defaultOption.value },
      fields,
    );
  }

  return templatizedAPIConfig;
};

const builders = {
  buildIterationKey,
  buildApiConfigToRestAPI,
  buildNewApiConfigToAddStep,
  buildSourceConfigFromPropsToRestAPI,
  buildNewApiConfigToRemoveStep,
  buildParameterizedURLs,
  buildTemplatizedAPIConfig,
  buildConfigToCreateRestAPISource,
  buildSourceConfigToRestAPIFromDefaults,
};
/******************************* VALIDATORS **********************************
 *****************************************************************************/
const validateDataPath = (args: any) => {
  const {
    stageAPIConfig,
    stageIndex = 0,
    apiResponseByStage,
    dataPathValidationResultByStage,
  } = args;
  const dataPaths = getDataPathFromAPIConfig(stageAPIConfig);

  if (_.isEmpty(dataPaths)) {
    return {
      apiResponseUpdateTime: Date.now(),
      dataPathValidationResultByStage: {
        ...dataPathValidationResultByStage,
        [stageIndex]: {
          status: true,
        },
      },
    };
  }

  return {
    apiResponseUpdateTime: Date.now(),
    dataPathValidationResultByStage: {
      ...dataPathValidationResultByStage,
      [stageIndex]: ValidationUtils.validateDataPath(
        dataPaths, // dataPathsByFieldName,
        apiResponseByStage[stageIndex], // apiResponse,
        stageAPIConfig,
      ),
    },
  };
};

type fnRequiredFieldsToRestAPI = (param: {
  fields?: Field[];
  /**
   * SourceConfig, ApiConfig, TemplatizedApiConfig
   */
  data: any;
}) => { error: boolean; message?: string };

const validateRequiredFieldsToRestAPI: fnRequiredFieldsToRestAPI = (param) => {
  const { data, fields = null } = param;
  const missingFields = ValidationUtils.getEmptyRequiredFields(fields, data);

  if (!isEmpty(missingFields)) {
    const errorMessage = reduce(
      missingFields,
      (message, field) => `${message} ${field.display_name},`,
      'Please enter the following required fields: ',
    );

    return {
      error: true,
      message: errorMessage.substring(0, errorMessage.length - 1),
    };
  }

  return { error: false };
};

const validateConfigureRestSourceRequiredFields = ({
  isPaginated,
  stageAPIConfig,
  connectionFields,
}) => {
  let fieldNames = ['url.template'];

  if (isPaginated) {
    const iterationType = stageAPIConfig['iteration.type'];
    fieldNames = fieldNames.concat(ITERATION_TYPE_TO_FIELDS_MAP[iterationType]);
  }

  const fields = _.map(fieldNames, (name) => connectionFields[name]);
  return validateRequiredFieldsToRestAPI({
    data: stageAPIConfig,
    fields: fields,
  });
};

const validators = {
  validateDataPath,
  validateRequiredFieldsToRestAPI,
  validateConfigureRestSourceRequiredFields,
};
/******************************************************************************

 *****************************************************************************/

export const RestAPIUtil = { builders, validators };
